import { Combobox, TextInput, useCombobox } from '@any-ui-react/core';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ProbationReviewStatusBadge } from '@/common/components/employees/probation-review';
import { EmployeeStatusBadge, PerformanceReviewStageBadge } from '@/common/components/shared/badge';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { QuickAccessCategory, type QuickAccessSearchQuery, useQuickAccessSearchQuery } from '@/common/graphql';

const DEBOUNCE_MS = 300;
const MIN_KEYWORD_LENGTH = 2;
const SEARCH_PREFIX_NUMBER = '#';
const SEARCH_PREFIX_PAGE = '/';
const SEARCH_PREFIXES = [SEARCH_PREFIX_NUMBER, SEARCH_PREFIX_PAGE];

const highlightName = (result: QuickAccessSearchQuery['quickAccessSearch'][number], keyword: string): ReactNode => {
  let name = '';
  switch (result.__typename) {
    case 'QuickAccessSearchResultEmployee':
    case 'QuickAccessSearchResultProbationReview': {
      name = result.employee.generalInformation.preferredFullName;
      break;
    }
    case 'QuickAccessSearchResultPerformanceReview': {
      name = result.performanceReview.employee.generalInformation.preferredFullName;
      break;
    }
    case 'QuickAccessSearchResultPage': {
      name = result.name;
    }
  }

  if (!keyword) return <>{name}</>;
  const parts: ReactNode[] = [];
  const lowerName = name.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  let lastIndex = 0;
  let matchIndex = lowerName.indexOf(lowerKeyword);
  let key = 0;
  while (matchIndex !== -1) {
    if (matchIndex > lastIndex) parts.push(name.slice(lastIndex, matchIndex));
    parts.push(
      <mark key={key++} className="bg-green-1 font-medium rounded-sm not-italic">
        {name.slice(matchIndex, matchIndex + keyword.length)}
      </mark>,
    );
    lastIndex = matchIndex + keyword.length;
    matchIndex = lowerName.indexOf(lowerKeyword, lastIndex);
  }
  if (lastIndex < name.length) parts.push(name.slice(lastIndex));
  return <>{parts}</>;
};

const categoryIcons: Record<QuickAccessCategory, ReactNode> = {
  [QuickAccessCategory.Employee]: <MaterialIcons className="cursor-pointer" iconName="groups" fill={true} />,
  [QuickAccessCategory.PerformanceReview]: <MaterialIcons className="cursor-pointer" iconName="star" fill={true} />,
  [QuickAccessCategory.ProbationReview]: (
    <MaterialIcons className="cursor-pointer" iconName="control_camera" fill={true} />
  ),
  [QuickAccessCategory.Page]: <MaterialIcons className="cursor-pointer" iconName="article" />,
};
const categoryNames: Partial<Record<QuickAccessCategory, string>> = {
  [QuickAccessCategory.Employee]: 'Employee',
  [QuickAccessCategory.PerformanceReview]: 'Performance Review',
  [QuickAccessCategory.ProbationReview]: 'Probation Review',
};

const leftSectionIcon = (keyword: string) => {
  if (keyword.startsWith(SEARCH_PREFIX_NUMBER)) {
    return 'numbers';
  } else if (keyword.startsWith(SEARCH_PREFIX_PAGE)) {
    return 'article';
  }

  return 'search';
};

export const QuickAccess = ({ onSelect, autoFocus = false }: { onSelect?: () => void; autoFocus?: boolean }) => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]);

  const shouldSearch = debouncedKeyword.length >= MIN_KEYWORD_LENGTH && !SEARCH_PREFIXES.includes(debouncedKeyword);

  const { data, loading } = useQuickAccessSearchQuery({
    variables: { keyword: debouncedKeyword },
    skip: !shouldSearch,
    fetchPolicy: 'cache-and-network',
  });

  const results = data?.quickAccessSearch ?? [];

  const handleOptionSubmit = useCallback(
    (url: string) => {
      combobox.closeDropdown();
      setKeyword('');
      setDebouncedKeyword('');
      navigate(url);
      onSelect?.();
    },
    [combobox, navigate, onSelect],
  );

  const handleInputChange = (value: string) => {
    setKeyword(value);
    if (value.length >= MIN_KEYWORD_LENGTH && !SEARCH_PREFIXES.includes(value)) {
      combobox.openDropdown();
    } else {
      combobox.closeDropdown();
    }
  };

  const showDropdown = combobox.dropdownOpened && keyword.length >= MIN_KEYWORD_LENGTH;

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <TextInput
          ref={inputRef}
          className="mx-2 my-2"
          leftSection={<MaterialIcons iconName={leftSectionIcon(keyword)} fontSize="18px" className="text-gray-5" />}
          placeholder="Quick access"
          value={keyword}
          onChange={e => handleInputChange(e.currentTarget.value)}
          onFocus={() => {
            if (keyword.length >= MIN_KEYWORD_LENGTH) combobox.openDropdown();
          }}
          onBlur={() => combobox.closeDropdown()}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              combobox.closeDropdown();
              setKeyword('');
            }
          }}
        />
      </Combobox.Target>

      {showDropdown && (
        <Combobox.Dropdown className="shadow-dropdown max-h-[calc(100vh-56px-32px-48px-24px)] overflow-auto">
          <Combobox.Options>
            {loading && results.length === 0 ? (
              <Combobox.Empty>
                <span className="text-sm text-gray-5">Searching...</span>
              </Combobox.Empty>
            ) : results.length === 0 ? (
              <Combobox.Empty>
                <span className="text-sm text-gray-5">No results</span>
              </Combobox.Empty>
            ) : (
              results.map((result, index) => (
                <Combobox.Option key={index} value={result.uri} className="px-2 py-2 cursor-pointer hover:bg-gray-0">
                  <div className="flex items-start gap-x-1.5">
                    <div className="mt-0.5">{categoryIcons[result.category]}</div>
                    <div>
                      <span className="text-sm">{highlightName(result, debouncedKeyword)}</span>
                      {(result.__typename === 'QuickAccessSearchResultEmployee' ||
                        result.__typename === 'QuickAccessSearchResultProbationReview') && (
                        <span className="text-gray-7">&nbsp;(#{result.id})</span>
                      )}
                      {result.__typename === 'QuickAccessSearchResultPerformanceReview' && (
                        <span className="text-zinc-700 text-xs">&nbsp;[#{result.performanceReview.employee.id}]</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-5.5 flex flex-wrap gap-1">
                    {categoryNames[result.category] && (
                      <div className="text-gray-7">{categoryNames[result.category]}</div>
                    )}
                    {result.__typename === 'QuickAccessSearchResultPage' && (
                      <div className="text-gray-7">{result.pageCategory}</div>
                    )}
                    {result.__typename === 'QuickAccessSearchResultEmployee' && (
                      <EmployeeStatusBadge status={result.employee.status} size="xs" />
                    )}
                    {result.__typename === 'QuickAccessSearchResultPerformanceReview' && (
                      <PerformanceReviewStageBadge
                        size="xs"
                        stage={result.performanceReview.setting.stage}
                        version={result.performanceReview.period.version}
                      />
                    )}
                    {result.__typename === 'QuickAccessSearchResultProbationReview' &&
                      result.employee.probationReview && (
                        <ProbationReviewStatusBadge probationStatus={result.employee.probationReview.probationStatus} />
                      )}
                  </div>
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};
